import React from 'react'
import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'

import { localStorageKeys } from '@sketch/constants'
import { routes, deleteEntityFromCache } from '@sketch/modules-common'

import {
  Button,
  Form,
  Input,
  Modal,
  Text,
  ModalInjectedProps,
} from '@sketch/components'

import { useToast } from '@sketch/toasts'

import { getItem, removeItem } from '@sketch/utils'

import {
  useDeleteWorkspaceMutation,
  GetWorkspacesDocument,
} from '@sketch/gql-types'

interface DeleteWorkspaceModalProps extends ModalInjectedProps {
  id: string
  name: string
}

/**
 * DeleteWorkspaceModal.
 *
 * This component shows a modal to the user with the ability of deleting a workspace.
 * To properly work this component should be call through the ModalContext API.
 *
 */
export const DeleteWorkspaceModal: React.FC<DeleteWorkspaceModalProps> = props => {
  const { id, name, hideModal } = props

  const { showToast } = useToast()
  const history = useHistory()

  const [deleteWorkspace, { loading }] = useDeleteWorkspaceMutation({
    variables: { workspaceId: id },
    onError: 'show-toast',
    update: (cache, { data }) => {
      /**
       * BohemianCoding/Cloud#1113
       * Delete the workspace from the apollo cache directly.
       *
       * It seems that re-fetching the workspaces list only helps with the workspace selector,
       * the user can still access the workspace information of a deleted workspace if we go back in the history,
       * because it's still in cache and we need to delete it.
       *
       * Currently being discussed here is the issue of "Cache invalidation & deletion" 😱.
       * https://github.com/apollographql/apollo-feature-requests/issues/4.
       *
       * There's still no correct solution for this, only work-arounds like this one.
       */
      if (data?.deleteWorkspace) {
        deleteEntityFromCache(cache, data.deleteWorkspace.deletedWorkspace)
      }
    },
    onCompleted: data => {
      const { deletedWorkspace } = data.deleteWorkspace || {}

      const storedWorkspaceId = getItem(localStorageKeys.lastWorkspaceIdKey)

      if (storedWorkspaceId === deletedWorkspace?.identifier) {
        removeItem(localStorageKeys.lastWorkspaceIdKey)
      }

      history.push(routes.ENTRY.create({}))

      showToast('Your workspace was successfully deleted.', 'positive')
      hideModal()
    },
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetWorkspacesDocument }],
  })

  return (
    <Modal onCancel={hideModal}>
      <Modal.Header>Delete the “{name}” Workspace?</Modal.Header>
      <Formik
        initialValues={{ name: '' }}
        onSubmit={async () => deleteWorkspace()}
      >
        {({ handleBlur, handleChange, values }) => (
          <Form>
            <Modal.Body>
              <p>
                All Workspace documents will be permanently deleted. Active
                Workspace licenses will be revoked, and access to billing
                information and invoices will be disabled.{' '}
                <Text fontWeight="bold" as="span">
                  You can&apos;t undo this action.
                </Text>
              </p>

              <p>
                If you&apos;re sure about deleting, enter the Workspace name to
                confirm.
              </p>
              <Form.Field name="name">
                <Input
                  placeholder="Workspace Name"
                  name="name"
                  type="text"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values['name']}
                />
              </Form.Field>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={hideModal}
                type="button"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                disabled={name !== values['name']}
                variant="negative-secondary"
                type="submit"
                loading={loading}
              >
                Delete
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}
